import {
  CAMPOS_PII_CLIENTE,
  PERMISSOES,
  perfilFormSchema,
  rotuloSetores,
  type CampoPiiCliente,
  type CategoriaComMotivos,
  type MensagemRapidaResumo,
  type Permissao,
  type PerfilForm,
  type PerfilResumo,
} from '@mymaxx/shared';
import { Aviso, Botao, CampoTexto, Modal } from '@mymaxx/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { ErroApi } from '../lib/api';
import { gestaoApi } from '../lib/gestao-api';

/** Agrupa as permissões por área, para a tela não virar uma lista de 20 caixinhas soltas. */
const GRUPOS: { titulo: string; prefixos: string[] }[] = [
  { titulo: 'Atendimento', prefixos: ['conversas.', 'clientes.'] },
  {
    titulo: 'Gestão',
    prefixos: [
      'setores.',
      'usuarios.',
      'perfis.',
      'fluxos.',
      'mensagens_rapidas.',
      'configuracoes.',
    ],
  },
  { titulo: 'Relatórios e privacidade', prefixos: ['relatorios.', 'auditoria.', 'lgpd.'] },
];

const ROTULO_CAMPO: Record<CampoPiiCliente, string> = {
  nome: 'Nome',
  cpf: 'CPF/CNPJ',
  telefone: 'Telefone',
  email: 'E-mail',
  endereco: 'Endereço',
};

const FORM_VAZIO: PerfilForm = {
  nome: '',
  descricao: '',
  permissoes: ['conversas.atender'],
  restringirCatalogos: false,
  motivosPermitidos: [],
  mensagensPermitidas: [],
  visibilidadeCampos: {
    nome: 'completo',
    cpf: 'mascarado',
    telefone: 'mascarado',
    email: 'mascarado',
    endereco: 'completo',
  },
};

export function Perfis() {
  const queryClient = useQueryClient();
  const [editando, setEditando] = useState<{
    id: string | null;
    sistema: boolean;
    form: PerfilForm;
  } | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const perfis = useQuery({ queryKey: ['perfis'], queryFn: gestaoApi.listarPerfis });
  const motivos = useQuery({ queryKey: ['motivos'], queryFn: gestaoApi.listarMotivos });
  const respostas = useQuery({
    queryKey: ['mensagens-rapidas'],
    queryFn: gestaoApi.listarMensagens,
  });
  const recarregar = () => void queryClient.invalidateQueries({ queryKey: ['perfis'] });
  const tratarErro = (e: unknown) =>
    setErro(e instanceof ErroApi ? e.mensagem : 'Não foi possível concluir');

  const salvar = useMutation({
    mutationFn: ({ id, form }: { id: string | null; form: PerfilForm }) =>
      id ? gestaoApi.atualizarPerfil(id, form) : gestaoApi.criarPerfil(form),
    onSuccess: () => {
      recarregar();
      setEditando(null);
      setErro(null);
    },
    onError: tratarErro,
  });

  const excluir = useMutation({
    mutationFn: (id: string) => gestaoApi.excluirPerfil(id),
    onSuccess: recarregar,
    onError: tratarErro,
  });

  function abrirEdicao(p: PerfilResumo) {
    setEditando({
      id: p.id,
      sistema: p.sistema,
      form: {
        nome: p.nome,
        descricao: p.descricao ?? '',
        permissoes: p.permissoes,
        restringirCatalogos: p.restringirCatalogos,
        motivosPermitidos: p.motivosPermitidos,
        mensagensPermitidas: p.mensagensPermitidas,
        visibilidadeCampos: p.visibilidadeCampos,
      },
    });
    setErro(null);
  }

  function enviar() {
    if (!editando) return;
    const parse = perfilFormSchema.safeParse(editando.form);
    if (!parse.success) {
      setErro(parse.error.issues[0]?.message ?? 'Confira os campos');
      return;
    }
    salvar.mutate({ id: editando.id, form: parse.data });
  }

  /** Marca/desmarca um item da lista de motivos ou respostas liberados. */
  function alternarCatalogo(
    campo: 'motivosPermitidos' | 'mensagensPermitidas',
    ids: string[],
    marcada: boolean,
  ) {
    if (!editando) return;
    const atuais = new Set(editando.form[campo]);
    for (const id of ids) {
      if (marcada) atuais.add(id);
      else atuais.delete(id);
    }
    setEditando({ ...editando, form: { ...editando.form, [campo]: [...atuais] } });
  }

  function alternarPermissao(permissao: string, marcada: boolean) {
    if (!editando) return;
    const atuais = new Set(editando.form.permissoes);
    if (marcada) atuais.add(permissao);
    else atuais.delete(permissao);
    setEditando({ ...editando, form: { ...editando.form, permissoes: [...atuais] } });
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Perfis e permissões</h1>
          <p className="text-sm text-neutral-500">
            O que cada função pode fazer e quais dados do cliente enxerga.
          </p>
        </div>
        <Botao
          onClick={() => {
            setEditando({ id: null, sistema: false, form: { ...FORM_VAZIO } });
            setErro(null);
          }}
        >
          <Plus size={16} /> Novo perfil
        </Botao>
      </header>

      {erro && !editando && <Aviso tipo="erro">{erro}</Aviso>}

      <ul className="grid gap-3 md:grid-cols-2">
        {perfis.data?.map((p) => (
          <li
            key={p.id}
            className="rounded-card border border-neutral-200 p-4 dark:border-neutral-800"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="flex items-center gap-2 font-medium">
                  {p.nome}
                  {p.sistema && (
                    <span title="Perfil padrão do sistema" className="text-neutral-400">
                      <Lock size={13} />
                    </span>
                  )}
                </p>
                {p.descricao && <p className="text-sm text-neutral-500">{p.descricao}</p>}
              </div>
              <span className="shrink-0 text-xs text-neutral-500">
                {p.totalUsuarios} usuário(s)
              </span>
            </div>

            <p className="mt-2 text-xs text-neutral-500">
              {p.restringirCatalogos
                ? `${p.motivosPermitidos.length} motivo(s) e ${p.mensagensPermitidas.length} resposta(s)`
                : 'todos os motivos e respostas'}
            </p>
            <p className="text-xs text-neutral-500">
              {p.permissoes.length} permissões · CPF{' '}
              {p.visibilidadeCampos.cpf === 'completo'
                ? 'visível'
                : p.visibilidadeCampos.cpf === 'mascarado'
                  ? 'mascarado'
                  : 'oculto'}
            </p>

            <div className="mt-3 flex gap-2">
              <Botao variante="secundario" onClick={() => abrirEdicao(p)}>
                <Pencil size={14} /> Editar
              </Botao>
              {!p.sistema && p.totalUsuarios === 0 && (
                <Botao
                  variante="secundario"
                  onClick={() => {
                    if (confirm(`Excluir o perfil ${p.nome}?`)) excluir.mutate(p.id);
                  }}
                >
                  <Trash2 size={14} /> Excluir
                </Botao>
              )}
            </div>
          </li>
        ))}
      </ul>

      <Modal
        aberto={!!editando}
        titulo={editando?.id ? 'Editar perfil' : 'Novo perfil'}
        largura="larga"
        onFechar={() => setEditando(null)}
        acoes={
          <>
            <Botao variante="secundario" onClick={() => setEditando(null)}>
              Cancelar
            </Botao>
            <Botao onClick={enviar} carregando={salvar.isPending}>
              Salvar
            </Botao>
          </>
        }
      >
        {editando && (
          <form
            className="space-y-5"
            onSubmit={(e) => {
              e.preventDefault();
              enviar();
            }}
            noValidate
          >
            <CampoTexto
              rotulo="Nome do perfil"
              autoFocus
              disabled={editando.sistema}
              value={editando.form.nome}
              onChange={(e) =>
                setEditando({ ...editando, form: { ...editando.form, nome: e.target.value } })
              }
              ajuda={editando.sistema ? 'Perfis padrão não podem ser renomeados.' : undefined}
            />
            <CampoTexto
              rotulo="Descrição"
              value={editando.form.descricao ?? ''}
              onChange={(e) =>
                setEditando({ ...editando, form: { ...editando.form, descricao: e.target.value } })
              }
            />

            <fieldset>
              <legend className="mb-2 text-sm font-medium">Permissões</legend>
              <div className="space-y-4">
                {GRUPOS.map((grupo) => {
                  const permissoes = Object.entries(PERMISSOES).filter(([chave]) =>
                    grupo.prefixos.some((p) => chave.startsWith(p)),
                  );
                  return (
                    <div key={grupo.titulo}>
                      <p className="mb-1 text-xs font-medium text-neutral-500 uppercase">
                        {grupo.titulo}
                      </p>
                      <div className="grid gap-1 sm:grid-cols-2">
                        {permissoes.map(([chave, descricao]) => (
                          <label key={chave} className="flex items-start gap-2 text-sm">
                            <input
                              type="checkbox"
                              className="mt-0.5"
                              checked={editando.form.permissoes.includes(chave as Permissao)}
                              onChange={(e) => alternarPermissao(chave, e.target.checked)}
                            />
                            <span>{descricao}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </fieldset>

            <fieldset>
              <legend className="mb-1 text-sm font-medium">Motivos e respostas disponíveis</legend>
              <p className="mb-2 text-xs text-neutral-500">
                Por padrão o perfil vê todos — inclusive os criados depois. Restrinja apenas quando
                precisar limitar o que este perfil pode usar no atendimento.
              </p>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={editando.form.restringirCatalogos}
                  onChange={(e) =>
                    setEditando({
                      ...editando,
                      form: { ...editando.form, restringirCatalogos: e.target.checked },
                    })
                  }
                />
                Limitar a itens específicos
              </label>

              {editando.form.restringirCatalogos && (
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <div className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-800">
                    <p className="mb-2 text-xs font-medium text-neutral-500 uppercase">
                      Motivos de encerramento
                    </p>
                    <div className="max-h-56 space-y-2 overflow-y-auto">
                      {motivos.data?.map((categoria: CategoriaComMotivos) => {
                        const ids = categoria.motivos.map((m) => m.id);
                        const marcados = ids.filter((id) =>
                          editando.form.motivosPermitidos.includes(id),
                        );
                        return (
                          <div key={categoria.id}>
                            <label className="flex items-center gap-2 text-sm font-medium">
                              <input
                                type="checkbox"
                                checked={ids.length > 0 && marcados.length === ids.length}
                                ref={(el) => {
                                  if (el) {
                                    el.indeterminate =
                                      marcados.length > 0 && marcados.length < ids.length;
                                  }
                                }}
                                onChange={(e) =>
                                  alternarCatalogo('motivosPermitidos', ids, e.target.checked)
                                }
                              />
                              {categoria.nome}
                            </label>
                            {categoria.motivos.map((m) => (
                              <label
                                key={m.id}
                                className="flex items-center gap-2 pl-5 text-sm text-neutral-600 dark:text-neutral-400"
                              >
                                <input
                                  type="checkbox"
                                  checked={editando.form.motivosPermitidos.includes(m.id)}
                                  onChange={(e) =>
                                    alternarCatalogo('motivosPermitidos', [m.id], e.target.checked)
                                  }
                                />
                                {m.nome}
                              </label>
                            ))}
                          </div>
                        );
                      })}
                      {motivos.data?.length === 0 && (
                        <p className="text-sm text-neutral-500">Nenhum motivo cadastrado.</p>
                      )}
                    </div>
                  </div>

                  <div className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-800">
                    <p className="mb-2 text-xs font-medium text-neutral-500 uppercase">
                      Respostas prontas
                    </p>
                    <div className="max-h-56 space-y-1 overflow-y-auto">
                      {respostas.data?.map((r: MensagemRapidaResumo) => (
                        <label key={r.id} className="flex items-start gap-2 text-sm">
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={editando.form.mensagensPermitidas.includes(r.id)}
                            onChange={(e) =>
                              alternarCatalogo('mensagensPermitidas', [r.id], e.target.checked)
                            }
                          />
                          <span>
                            {r.titulo}
                            <span className="block text-xs text-neutral-500">
                              /{r.atalho} · {rotuloSetores(r.setores)}
                            </span>
                          </span>
                        </label>
                      ))}
                      {respostas.data?.length === 0 && (
                        <p className="text-sm text-neutral-500">Nenhuma resposta cadastrada.</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </fieldset>

            <fieldset>
              <legend className="mb-1 text-sm font-medium">
                Dados do cliente que este perfil vê
              </legend>
              <p className="mb-2 text-xs text-neutral-500">
                Vale para a tela do atendente. Dados do serviço (plano, endereço da instalação)
                seguem o campo Endereço.
              </p>
              <div className="space-y-1">
                {CAMPOS_PII_CLIENTE.map((campo) => (
                  <div key={campo} className="flex items-center gap-3 text-sm">
                    <span className="w-24">{ROTULO_CAMPO[campo]}</span>
                    <select
                      value={editando.form.visibilidadeCampos[campo]}
                      aria-label={`Visibilidade de ${ROTULO_CAMPO[campo]}`}
                      onChange={(e) =>
                        setEditando({
                          ...editando,
                          form: {
                            ...editando.form,
                            visibilidadeCampos: {
                              ...editando.form.visibilidadeCampos,
                              [campo]: e.target.value as 'oculto' | 'mascarado' | 'completo',
                            },
                          },
                        })
                      }
                      className="rounded border border-neutral-300 px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-900"
                    >
                      <option value="completo">Completo</option>
                      <option value="mascarado">Mascarado</option>
                      <option value="oculto">Oculto</option>
                    </select>
                  </div>
                ))}
              </div>
            </fieldset>

            {erro && <Aviso tipo="erro">{erro}</Aviso>}
          </form>
        )}
      </Modal>
    </div>
  );
}
